import { CallTakerStationOption } from '@mark43/cad-api';
import React, { useCallback } from 'react';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useBroadcast } from '../common/broadcast-channel/use-broadcast';
import { CallTakerStationContext } from './call-taker-station-context';
import { electronBridge } from '../electron-bridge/electron-bridge';
import { useApplicationType } from '../common/hooks/use-application-type';

export function CallTakerStationProvider(props: {
    children: React.ReactElement;
}): React.ReactElement {
    const { isElectron } = useApplicationType();
    const bootstrapState = useBootstrapState();

    const callTakerStationOptions: CallTakerStationOption[] =
        bootstrapState.selectCallTakerStationView.callTakerStations;

    const [currentCallTakerStation, setCurrentCallTakerStation] = React.useState<
        CallTakerStationOption | undefined
    >(
        // In electron we initialize this to undefined and assign the correct value asynchronously in an effect below
        // On web we initialize this based on a value in the database that comes down in the bootstrap
        isElectron ? undefined : bootstrapState.selectCallTakerStationView.currentCallTakerStation
    );

    const assignCallTakerStationElectronFailedHandler = React.useRef<(() => void) | null>();

    const onCallTakerStationId = useCallback(
        (stationId: number | undefined) => {
            setCurrentCallTakerStation(
                stationId
                    ? callTakerStationOptions.find(
                          (callTakerStationOption) => callTakerStationOption.value === stationId
                      )
                    : undefined
            );
        },
        [callTakerStationOptions]
    );

    React.useEffect(() => {
        if (electronBridge) {
            if ('onCallTakerStationId' in electronBridge) {
                electronBridge.onCallTakerStationId(onCallTakerStationId);
            }
            electronBridge.getAssignedCallTakerStationId().then(onCallTakerStationId);
        }
    }, [callTakerStationOptions, isElectron, onCallTakerStationId]);

    const { postMessage } = useBroadcast('legacy-store-broadcast', (message) => {
        if (message.type === 'CALL_TAKER_STATION_CHANGED') {
            !message.payload
                ? setCurrentCallTakerStation(undefined)
                : setCurrentCallTakerStation(
                      callTakerStationOptions.find(
                          (callTakerStationOption) =>
                              callTakerStationOption.value === message.payload
                      )
                  );
        } else if (message.type === 'CALL_TAKER_STATION_FAILURE') {
            assignCallTakerStationElectronFailedHandler.current?.();
        }
    });

    const value = React.useMemo(() => {
        return {
            currentStation: currentCallTakerStation,
            setCurrentStation: setCurrentCallTakerStation,
            assignCallTakerStationElectron: (callTakerStationId: number | undefined) => {
                if (electronBridge && 'changeCallTakerStationId' in electronBridge) {
                    electronBridge.changeCallTakerStationId(callTakerStationId);
                } else {
                    postMessage({
                        type: 'CHANGE_CALL_TAKER_STATION',
                        payload: callTakerStationId,
                    });
                }
            },
            registerAssignCallTakerStationElectronFailedHandler: (handler: (() => void) | null) => {
                assignCallTakerStationElectronFailedHandler.current = handler;
            },
        };
    }, [currentCallTakerStation, postMessage]);

    return (
        <CallTakerStationContext.Provider value={value}>
            {props.children}
        </CallTakerStationContext.Provider>
    );
}
